import { enumLabel } from '../utils/enumLabel';
import type { DataBasis } from '../utils/dataBasis';
import { DEVELOPMENT_PROJECT_TYPE_OPTIONS } from './taxonomy';
export interface ViolationDetail {
  violationType: string;
  // Shared per-violation
  perpetratorKnown: boolean;
  perpetratorTypes: string[];
  perpetratorDetails: string;
  perpetratorAccountabilityStatus: string; // '' = not set
  legalCaseFiled: boolean | null;
  legalCaseDetails: string;
  legalCaseWhyNot: string;
  stateActionUndertaken: boolean | null;
  stateActionDetails: string;
  stateActionWhyNot: string;
  // Criminalization-specific
  criminalizationTypes: string[];
  criminalizationTriggers: string[];
  legalInstrumentTypes: string[];
  proceedingType: string; // '' = not set
  isRetaliatoryProceeding: boolean | null;
  whyVictimsCriminalized: string;
  harassmentPrior: boolean | null;
  harassmentPriorDetails: string;
  accusations: string;
  policiesOrLawsUsed: string;
  whoFiledAccusations: string[];
  whoFiledAccusationsDetails: string;
  accusationsLegallyFiled: boolean | null;
  courtFilingLocation: string;
  courtFilingDetails: string;
  decisionOnAccusations: boolean | null;
  decisionDetails: string;
  victimsInPrison: boolean | null;
  prisonConditions: string;
  victimsSituationIfNotInPrison: string;
  // Killing-specific
  whyVictimsKilled: string;
  howKillingCarriedOut: string;
  // Other-specific
  cause: string;
}

export const createEmptyViolationDetail = (violationType: string): ViolationDetail => ({
  violationType,
  perpetratorKnown: false,
  perpetratorTypes: [],
  perpetratorDetails: '',
  perpetratorAccountabilityStatus: '',
  legalCaseFiled: null,
  legalCaseDetails: '',
  legalCaseWhyNot: '',
  stateActionUndertaken: null,
  stateActionDetails: '',
  stateActionWhyNot: '',
  criminalizationTypes: [],
  criminalizationTriggers: [],
  legalInstrumentTypes: [],
  proceedingType: '',
  isRetaliatoryProceeding: null,
  whyVictimsCriminalized: '',
  harassmentPrior: null,
  harassmentPriorDetails: '',
  accusations: '',
  policiesOrLawsUsed: '',
  whoFiledAccusations: [],
  whoFiledAccusationsDetails: '',
  accusationsLegallyFiled: null,
  courtFilingLocation: '',
  courtFilingDetails: '',
  decisionOnAccusations: null,
  decisionDetails: '',
  victimsInPrison: null,
  prisonConditions: '',
  victimsSituationIfNotInPrison: '',
  whyVictimsKilled: '',
  howKillingCarriedOut: '',
  cause: '',
});

export interface CaseFormData {
  // Documenter Details
  documentorName: string;
  documentorOrganization: string;
  documentorEmail: string;
  documentorPhone: string;
  isPrimaryData: boolean; // derived from dataBasis on save; kept for the API mirror
  dataBasis: DataBasis;
  dataSource: string;
  dataUsageRestriction: string;
  willingToShareMoreInfo: boolean;
  isLDSFApplicant: boolean;
  ldsfPaymentCompletedDate: string;
  sourceInformation: string;

  // Incident Details
  incidentTime: string;
  description: string;
  violationTypes: string[];
  otherViolationTypes: string[];
  otherViolationTypeDetails: string;
  isCaseOngoing: boolean;
  caseStartDate: string;
  caseStartDatePrecision: 'DAY' | 'MONTH' | 'YEAR';
  caseEndDate: string;
  reportedDate: string;

  // Location
  country: string;
  region: string;
  city: string;
  specificLocation: string;
  latitude: string;
  longitude: string;

  // Indigenous Group
  indigenousGroup: string[];
  indigenousTerritory: string;

  // Victims
  victims: Array<{
    id?: string;
    isGroup: boolean;
    // false = a non-Indigenous victim (lawyer, protester, supporter …) targeted in the same
    // incident. Absent/true = Indigenous. Mirrors Victim.isIndigenous in prisma/schema.prisma.
    isIndigenous?: boolean;
    // Which violation(s) this victim was subjected to. Flat keys: a top-level
    // type (KILLING/CRIMINALIZATION) or an other-type value (e.g. ARBITRARY_DETENTION).
    violationTags?: string[];
    // Per-violation detail keyed by violation key. GROUP victims: affectedCount
    // plus optional gender/age counts (blank field = not disaggregated).
    // CRIMINALIZATION rows may also carry per-person outcome fields
    // (accusationOutcome, detentionType, detentionDays, dueProcessViolations).
    violationDemographics?: { [key: string]: { [field: string]: number | string | string[] } };
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    ageBracket?: string;
    gender?: string;
    dependentType?: string;
    dependentDetails?: string;
    communityRole?: string;
    communityRoleType?: string;
    indigenousGroupOfVictim?: string;
    additionalInfo?: string;
    // Group victim fields
    groupName?: string[];
    groupSize?: number;
    groupType?: string;
    affectedIndividualsCount?: number;
    affectedFamiliesCount?: number;
    affectedHomesCount?: number;
    affectedGroupsCount?: number;
    affectedCommunitiesCount?: number;
    affectedVillagesCount?: number;
    affectedMaleCount?: number;
    affectedFemaleCount?: number;
    affected0to12Count?: number;
    affected13to25Count?: number;
    affected26to35Count?: number;
    affected36to59Count?: number;
    affected60PlusCount?: number;
  }>;

  // Perpetrators
  perpetrators: Array<{
    id?: string;
    isOrganization: boolean;
    name: string;
    type: string;
    organizationHead?: string;
    affiliation?: string;
    position?: string;
    notes?: string;
  }>;

  // Violation Details (per-violation-type)
  violationDetails: ViolationDetail[];

  // Impact (case-level)
  familyImpact: string;
  communityImpact: string;
  impactTypes: string[];

  // Investigation & Advocacy (case-level)
  investigationConducted: boolean | null; // null = not asked
  investigationDetails: string;
  investigationWhyNot: string;
  advocacyUndertaken: boolean | null;
  advocacyDetails: string;
  advocacyWhyNot: string;

  // Development Projects (several per case; the first is the primary one)
  isRelatedToDevelopmentProject: boolean | null;
  developmentProjects: DevelopmentProjectForm[];

  // Metadata
  priority: number;
  tags: string[];
  isPublic: boolean;

  // Files
  files: CaseFile[];
}

export interface CaseFile {
  file: File;
  category: string;
  description: string;
  documentDate: string;
  authorSource: string;
  language: string;
  confidentiality: string;
  sensitivityWarnings: string[];
}

export const SENSITIVITY_FLAGS = [
  { value: 'GRAPHIC_CONTENT', label: 'Graphic images of injuries, death, or violence', prefix: '[GRAPHIC CONTENT]' },
  { value: 'SEXUAL_VIOLENCE', label: 'Content depicting or describing sexual violence', prefix: '[SENSITIVE - SEXUAL VIOLENCE]' },
  { value: 'MINORS', label: 'Content involving minors (under 18)', prefix: '[SENSITIVE - MINORS]' },
  { value: 'DISTRESSING', label: 'Content showing identifiable victims in distressing situations or that could cause retraumatization', prefix: '[DISTRESSING CONTENT]' },
];

export const initialFormData: CaseFormData = {
  documentorName: '',
  documentorOrganization: '',
  documentorEmail: '',
  documentorPhone: '',
  isPrimaryData: true,
  dataBasis: 'PRIMARY',
  dataSource: '',
  dataUsageRestriction: '',
  willingToShareMoreInfo: false,
  isLDSFApplicant: false,
  ldsfPaymentCompletedDate: '',
  sourceInformation: '',
  incidentTime: '',
  description: '',
  violationTypes: [],
  otherViolationTypes: [],
  otherViolationTypeDetails: '',
  isCaseOngoing: false,
  caseStartDate: '',
  caseStartDatePrecision: 'DAY',
  caseEndDate: '',
  reportedDate: '',
  country: '',
  region: '',
  city: '',
  specificLocation: '',
  latitude: '',
  longitude: '',
  indigenousGroup: [],
  indigenousTerritory: '',
  victims: [],
  perpetrators: [], // named entities are optional; kinds are recorded per violation
  violationDetails: [],
  familyImpact: '',
  communityImpact: '',
  impactTypes: [],
  investigationConducted: null,
  investigationDetails: '',
  investigationWhyNot: '',
  advocacyUndertaken: null,
  advocacyDetails: '',
  advocacyWhyNot: '',
  isRelatedToDevelopmentProject: null,
  developmentProjects: [],
  priority: 1,
  tags: [],
  isPublic: false,
  files: []
};

export const VIOLATION_TYPES = [
  'KILLING',
  'CRIMINALIZATION',
  'OTHER'
];

export const PERPETRATOR_TYPE_GROUPS = [
  {
    label: 'State Actors',
    types: [
      { value: 'NATIONAL_GOVERNMENT_OFFICIAL', label: 'National Government Official' },
      { value: 'FEDERAL_GOVERNMENT_OFFICIAL', label: 'Federal Government Official' },
      { value: 'LOCAL_GOVERNMENT_OFFICIAL', label: 'Local Government Official' },
      { value: 'JUDICIARY_OFFICIAL', label: 'Judiciary Official' },
      { value: 'PRISON_OFFICIAL', label: 'Prison Official' },
      { value: 'POLICE', label: 'Police' },
      { value: 'ARMED_FORCES', label: 'Armed Forces' },
      { value: 'INTELLIGENCE_AGENCY', label: 'Intelligence Agency' },
      { value: 'FOREST_OFFICIAL', label: 'Forest Official' },
    ],
  },
  {
    label: 'Non-State Actors',
    types: [
      { value: 'VIGILANTE_GROUPS', label: 'Vigilante Groups' },
      { value: 'GUERILLA_ARMIES_OFFICIALS', label: 'Guerilla Armies Officials' },
      { value: 'PRIVATE_INDIVIDUAL', label: 'Private Individual' },
      { value: 'ORGANIZED_CRIME', label: 'Organized Crime' },
      { value: 'RELIGIOUS_INSTITUTION', label: 'Religious Institution' },
      { value: 'PRIVATE_SECURITY', label: 'Private Security' },
    ],
  },
  {
    label: 'Business / Institutional',
    types: [
      { value: 'NATIONAL_LOCAL_CORPORATION', label: 'National / Local Corporation' },
      { value: 'TRANSNATIONAL_CORPORATION', label: 'Transnational Corporation' },
      { value: 'MULTILATERAL_BODY', label: 'Multilateral Body' },
      { value: 'INTERNATIONAL_CONSERVATION_ORGANIZATION', label: 'International Conservation Organization' },
      { value: 'INTERNATIONAL_FINANCIAL_INSTITUTION', label: 'International Financial Institution' },
    ],
  },
  {
    label: 'Fallback',
    types: [
      { value: 'OTHER', label: 'Other' },
    ],
  },
];

// Flat list for backward compatibility
export const PERPETRATOR_TYPES = PERPETRATOR_TYPE_GROUPS.flatMap(g => g.types);

export const OTHER_VIOLATION_TYPES = [
  { value: 'ARBITRARY_DETENTION', label: 'Arbitrary Detention' },
  { value: 'CULTURAL_DESTRUCTION', label: 'Cultural Destruction' },
  { value: 'ENFORCED_DISAPPEARANCE', label: 'Enforced Disappearance' },
  { value: 'ENVIRONMENTAL_DESTRUCTION', label: 'Environmental Destruction' },
  { value: 'FORCED_DISPLACEMENT', label: 'Forced Displacement / Eviction' },
  { value: 'KIDNAPPING', label: 'Kidnapping' },
  { value: 'LAND_GRABBING', label: 'Land Grabbing' },
  { value: 'ONLINE_ATTACKS', label: 'Online Attacks (Smear Campaign, Misinformation)' },
  { value: 'PHYSICAL_INJURY', label: 'Physical Injury / Beating / Torture' },
  { value: 'SEXUAL_VIOLENCE', label: 'Sexual Violence and Gender-Based Violence' },
  { value: 'THREATS_AND_INTIMIDATION', label: 'Threats and Intimidation' },
  { value: 'VILIFICATION', label: 'Vilification' },
  { value: 'FPIC_VIOLATION', label: 'Free, Prior and Informed Consent Violation' },
  { value: 'LOSS_OF_LIVELIHOODS', label: 'Loss of Livelihoods' },
  { value: 'DISCRIMINATION', label: 'Discrimination' },
  { value: 'AERIAL_BOMBARDMENT', label: 'Aerial Bombing / Bombardment / Strafing' },
  { value: 'FAILURE_TO_FULFILL_ESC_RIGHTS', label: 'Failure to Fulfill Economic, Social and Cultural Rights' },
  { value: 'OTHER', label: 'Other' },
];

export const WHO_FILED_ACCUSATIONS_OPTIONS = [
  { value: 'NATIONAL_GOVERNMENT_OFFICIAL', label: 'National-level government official' },
  { value: 'FEDERAL_GOVERNMENT_OFFICIAL', label: 'Federal-level government official' },
  { value: 'LOCAL_GOVERNMENT_OFFICIAL', label: 'Local government official' },
  { value: 'JUDICIARY_OFFICIAL', label: 'Official in the judiciary' },
  { value: 'PRISON_OFFICIAL', label: 'Prison official' },
  { value: 'POLICE', label: 'Police' },
  { value: 'ARMED_FORCES', label: 'Armed forces' },
  { value: 'INTELLIGENCE_AGENCY', label: 'Intelligence agency; secret service' },
  { value: 'VIGILANTE_GROUPS', label: 'Vigilante groups; death squads' },
  { value: 'GUERILLA_ARMIES_OFFICIALS', label: 'Guerilla armies officials' },
  { value: 'NATIONAL_LOCAL_CORPORATION', label: 'National or local corporation' },
  { value: 'TRANSNATIONAL_CORPORATION', label: 'Transnational corporation' },
  { value: 'OTHER', label: 'Other' },
];

// Mirrors VictimGroupType in prisma/schema.prisma (filled when isGroup).
export const VICTIM_GROUP_TYPES = [
  { value: 'COMMUNITY', label: 'Community' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'MOVEMENT', label: 'Movement' },
  { value: 'FAMILY', label: 'Family' },
  { value: 'OTHER', label: 'Other' },
  // Kinds of non-Indigenous group — see NON_INDIGENOUS_GROUP_TYPE_VALUES.
  { value: 'LAWYERS', label: 'Lawyers' },
  { value: 'PROTESTERS', label: 'Protesters' },
  { value: 'JOURNALISTS', label: 'Journalists' },
  { value: 'HUMAN_RIGHTS_DEFENDERS', label: 'Human Rights Defenders' },
  { value: 'NGO_WORKERS', label: 'NGO Workers' },
  { value: 'CHURCH_WORKERS', label: 'Church Workers' },
  { value: 'ACADEME', label: 'Academe' },
  { value: 'STUDENTS', label: 'Students' },
  { value: 'SUPPORT_GROUP', label: 'Support Group' },
];

// Which VICTIM_GROUP_TYPES the form offers for each identity. The enum is one list in the
// database; an Indigenous group is a community/family/…, a non-Indigenous one is a kind of people.
export const INDIGENOUS_GROUP_TYPE_VALUES = ['COMMUNITY', 'ORGANIZATION', 'MOVEMENT', 'FAMILY', 'OTHER'];
export const NON_INDIGENOUS_GROUP_TYPE_VALUES = ['LAWYERS', 'PROTESTERS', 'JOURNALISTS', 'HUMAN_RIGHTS_DEFENDERS',
  'NGO_WORKERS', 'CHURCH_WORKERS', 'ACADEME', 'STUDENTS', 'SUPPORT_GROUP', 'ORGANIZATION', 'MOVEMENT', 'OTHER'];

// Mirrors CommunityRoleType in prisma/schema.prisma. Analytical category —
// the free-text communityRole keeps the local title (datu, timuay, cacique, lonko).
export const COMMUNITY_ROLE_TYPES = [
  { value: 'TRADITIONAL_AUTHORITY', label: 'Traditional authority' },
  { value: 'ELECTED_LEADER', label: 'Elected leader' },
  { value: 'ELDER', label: 'Elder' },
  { value: 'SPIRITUAL_LEADER', label: 'Spiritual leader' },
  { value: 'HRD_ACTIVIST', label: 'Human rights defender / activist' },
  { value: 'COMMUNICATOR_JOURNALIST', label: 'Communicator / journalist' },
  { value: 'LAWYER', label: 'Lawyer' },
  { value: 'INDIGENOUS_GUARD', label: 'Indigenous guard' },
  { value: 'WOMEN_LEADER', label: 'Women leader' },
  { value: 'YOUTH_LEADER', label: 'Youth leader' },
  { value: 'COMMUNITY_MEMBER', label: 'Community member' },
  { value: 'OTHER', label: 'Other' },
];

// Form of criminalization (mirrors CriminalizationType in prisma/schema.prisma).
// Coding rule: if the charges were dropped tomorrow, would the law still
// prohibit the activity? Yes -> EXERCISE_OF_RIGHTS.
export const CRIMINALIZATION_TYPES = [
  { value: 'EXERCISE_OF_RIGHTS', label: 'Criminalization of the exercise of rights (the law itself prohibits the practice)' },
  { value: 'DEFENDER_TARGETING', label: 'Targeting of defenders (generic laws applied to punish opposition)' },
];

// Mirrors CriminalizationTrigger in prisma/schema.prisma.
export const CRIMINALIZATION_TRIGGERS = [
  { value: 'PROTEST_OPPOSITION', label: 'Protest / opposition' },
  { value: 'LAND_TERRITORY_RESOURCE_DEFENSE', label: 'Defense of lands, territories or resources' },
  { value: 'EXERCISE_OF_TRADITIONAL_LIVELIHOOD', label: 'Exercise of traditional livelihood (hunting, fishing, farming, gathering)' },
  { value: 'CULTURAL_RELIGIOUS_PRACTICE', label: 'Cultural or religious practice' },
  { value: 'INDIGENOUS_GOVERNANCE_ACT', label: 'Act of indigenous governance' },
  { value: 'COMMUNITY_MEDIA', label: 'Community media / communication' },
  { value: 'LEGAL_ADVOCACY_REPORTING', label: 'Legal advocacy or reporting' },
  { value: 'EXERCISE_OF_POLITICAL_RIGHTS', label: 'Exercise of political rights' },
  { value: 'EXERCISE_OF_RIGHT_TO_IDENTITY', label: 'Exercise of the right to identity' },
  { value: 'PROVISION_OF_HUMANITARIAN_ASSISTANCE', label: 'Provision of humanitarian assistance' },
  { value: 'INNOCENT_DAILY_CONDUCT', label: 'Innocent daily conduct' },
  { value: 'OTHER', label: 'Other' },
];

// Mirrors LegalInstrumentType in prisma/schema.prisma. Category only —
// the specific statute names stay in policiesOrLawsUsed.
export const LEGAL_INSTRUMENT_TYPES = [
  { value: 'ANTI_TERRORISM', label: 'Anti-terrorism' },
  { value: 'NATIONAL_SECURITY', label: 'National security' },
  { value: 'SEDITION', label: 'Sedition' },
  { value: 'ORGANIZED_CRIME', label: 'Organized crime' },
  { value: 'ANTI_PROTEST_PUBLIC_ORDER', label: 'Anti-protest / public order' },
  { value: 'TRESPASS_USURPATION', label: 'Trespass / usurpation' },
  { value: 'CONSERVATION_WILDLIFE_FOREST', label: 'Conservation / wildlife / forest laws' },
  { value: 'PROPERTY_DAMAGE', label: 'Property damage (arson, sabotage, vandalism)' },
  { value: 'ORDINARY_CRIME_PROPERTY', label: 'Ordinary property crime (theft, robbery, extortion)' },
  { value: 'ORDINARY_CRIME_VIOLENCE', label: 'Ordinary violent crime (murder, assault, kidnapping)' },
  { value: 'DEFAMATION_CYBERLIBEL', label: 'Defamation / cyberlibel' },
  { value: 'CIVIL_INJUNCTION_CONTEMPT', label: 'Civil injunction / contempt' },
  { value: 'ADMINISTRATIVE_SANCTION', label: 'Administrative sanction (censorship, asset freezing, permit revocation, etc.)' },
  { value: 'OTHER', label: 'Other' },
];

// Mirrors ImpactType in prisma/schema.prisma — coded case-wide consequences.
// familyImpact / communityImpact stay as the narratives; displacement and
// livelihood loss remain in otherViolationTypes (not double-counted here).
export const IMPACT_TYPES = [
  { value: 'RESTRICTED_ACCESS_TO_LANDS_TERRITORIES_RESOURCES', label: 'Restricted access to lands, territories and resources' },
  { value: 'FOOD_INSECURITY', label: 'Food insecurity' },
  { value: 'PHYSICAL_HEALTH', label: 'Physical health impacts' },
  { value: 'MENTAL_HEALTH_PSYCHOSOCIAL', label: 'Mental health / psychosocial impacts' },
  { value: 'EDUCATION_DISRUPTION', label: 'Education disruption' },
  { value: 'FAMILY_SEPARATION', label: 'Family separation' },
  { value: 'INCREASED_CARE_BURDEN', label: 'Increased care burden' },
  { value: 'LEGAL_AND_ECONOMIC_COSTS', label: 'Legal and economic costs' },
  { value: 'LOSS_OF_LEADERSHIP_GOVERNANCE', label: 'Loss of leadership / governance' },
  { value: 'CULTURAL_SPIRITUAL_HARM', label: 'Cultural / spiritual harm' },
  { value: 'STIGMA_SOCIAL_EXCLUSION', label: 'Stigma / social exclusion' },
  { value: 'CHILLING_EFFECT', label: 'Chilling effect' },
  { value: 'MILITARIZATION_OF_TERRITORY', label: 'Militarization of territory' },
  { value: 'FORCED_INTO_HIDING', label: 'Forced into hiding' },
  { value: 'OTHER', label: 'Other' },
];

// Mirrors AccusationOutcome in prisma/schema.prisma — per-person outcome on a
// CRIMINALIZATION row. decisionOnAccusations stays as the case-level summary.
export const ACCUSATION_OUTCOMES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'DISMISSED', label: 'Dismissed' },
  { value: 'WITHDRAWN', label: 'Withdrawn' },
  { value: 'ACQUITTED', label: 'Acquitted' },
  { value: 'CONVICTED', label: 'Convicted' },
];

// Mirrors DetentionType in prisma/schema.prisma.
export const DETENTION_TYPES = [
  { value: 'PRE_TRIAL', label: 'Pre-trial detention' },
  { value: 'POST_CONVICTION', label: 'Post-conviction detention' },
  { value: 'ADMINISTRATIVE', label: 'Administrative detention (preventive detention, detention without charges)' },
  { value: 'HOUSE_ARREST', label: 'House arrest' },
];

// Mirrors DueProcessViolation in prisma/schema.prisma.
export const DUE_PROCESS_VIOLATIONS = [
  { value: 'NO_INTERPRETER', label: 'No interpreter' },
  { value: 'NO_COUNSEL', label: 'No legal counsel' },
  { value: 'DENIED_BAIL', label: 'Denied bail' },
  { value: 'PROLONGED_PRETRIAL', label: 'Prolonged pre-trial detention' },
  { value: 'MILITARY_OR_SPECIAL_COURT', label: 'Military or special court' },
  { value: 'INCOMMUNICADO', label: 'Incommunicado detention' },
  { value: 'TORTURE_ILL_TREATMENT', label: 'Torture or ill-treatment' },
  { value: 'NO_CULTURAL_ACCOMMODATION', label: 'No cultural accommodation' },
];

// Mirrors ProceedingType in prisma/schema.prisma.
export const PROCEEDING_TYPES = [
  { value: 'CRIMINAL', label: 'Criminal' },
  { value: 'CIVIL', label: 'Civil' },
  { value: 'ADMINISTRATIVE', label: 'Administrative' },
  { value: 'MIXED', label: 'Mixed' },
];

// Mirrors PerpetratorAccountabilityStatus in prisma/schema.prisma.
// Accountability for the violation committed against the victims (impunity axis).
export const PERPETRATOR_ACCOUNTABILITY_STATUSES = [
  { value: 'NO_INVESTIGATION', label: 'No investigation' },
  { value: 'STALLED', label: 'Investigation stalled' },
  { value: 'ONGOING', label: 'Investigation / prosecution ongoing' },
  { value: 'CHARGED', label: 'Perpetrator(s) charged' },
  { value: 'CONVICTED', label: 'Perpetrator(s) convicted' },
  { value: 'ACQUITTED', label: 'Perpetrator(s) acquitted' },
];

// Parent types a reviewer can pick; labels and definitions come from the codebook (types/taxonomy.ts).
export const DEVELOPMENT_PROJECT_TYPES = DEVELOPMENT_PROJECT_TYPE_OPTIONS;

// One development project on the form. A case can carry several; the backend stores them as
// DevelopmentProject rows and the first row is the primary project.
export interface DevelopmentProjectForm {
  id?: string;        // the row id when editing, so AI ledger paths (developmentProjects[<id>].name) survive a save
  type: string;       // parent code, '' when not chosen
  subtypes: string[]; // multi-select; only codes the parent allows
  name: string;
  description: string;
  startYear: string;  // text in the form, integer on the wire
  source: string;
  websiteLink: string;
}

export const emptyDevelopmentProject = (): DevelopmentProjectForm =>
  ({ type: '', subtypes: [], name: '', description: '', startYear: '', source: '', websiteLink: '' });

export const developmentProjectFromApi = (p: any): DevelopmentProjectForm => ({
  ...(p?.id ? { id: String(p.id) } : {}),
  type: p?.type || '',
  subtypes: Array.isArray(p?.subtypes) ? p.subtypes : [],
  name: p?.name || '',
  description: p?.description || '',
  startYear: p?.startYear ? String(p.startYear) : '',
  source: p?.source || '',
  websiteLink: p?.websiteLink || '',
});

// A case's project rows for the form. A record that still only has the legacy flat columns
// (developmentProjectType, developmentProjectName, …) becomes one row.
export const developmentProjectsFromCase = (c: any): DevelopmentProjectForm[] => {
  if (Array.isArray(c?.developmentProjects) && c.developmentProjects.length > 0) return c.developmentProjects.map(developmentProjectFromApi);
  if (c?.developmentProjectType || c?.developmentProjectName || c?.developmentProjectDescription) {
    return [developmentProjectFromApi({
      type: c.developmentProjectType, name: c.developmentProjectName, description: c.developmentProjectDescription,
      startYear: c.developmentProjectStartYear, source: c.developmentProjectSource, websiteLink: c.developmentProjectWebsiteLink,
    })];
  }
  return [];
};

export const isBlankDevelopmentProject = (p: DevelopmentProjectForm): boolean =>
  !p.type && p.subtypes.length === 0 && !p.name.trim() && !p.description.trim() && !p.startYear.trim() && !p.source.trim() && !p.websiteLink.trim();

// The wire shape of one project (POST/PUT /api/cases, field developmentProjects[]).
export const developmentProjectToApi = (p: DevelopmentProjectForm) => ({
  ...(p.id ? { id: p.id } : {}),
  type: p.type || null,
  subtypes: p.subtypes,
  name: p.name.trim() || null,
  description: p.description.trim() || null,
  startYear: p.startYear.trim() ? parseInt(p.startYear, 10) : null,
  source: p.source.trim() || null,
  websiteLink: p.websiteLink.trim() || null,
});

// Blank rows are dropped so an untouched "add a project" row never reaches the API.
export const developmentProjectsToApi = (rows: DevelopmentProjectForm[]) =>
  rows.filter((p) => !isBlankDevelopmentProject(p)).map(developmentProjectToApi);

// Stored values are the codebook letters; the labels are what a person reads.
export const AGE_BRACKETS = [
  { value: 'A', label: '0–12 years' },
  { value: 'B', label: '13–25 years' },
  { value: 'C', label: '26–35 years' },
  { value: 'D', label: '36–59 years' },
  { value: 'E', label: '60 years and above' }
];

export const DEPENDENT_TYPES = [
  { value: 'WD', label: 'With dependents' },
  { value: 'WOD', label: 'Without dependents' },
  { value: 'ND', label: 'No data' }
];

export const CONFIDENTIALITY_LEVELS = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'RESTRICTED', label: 'Restricted' },
];

export const DOCUMENT_CATEGORIES = [
  { value: 'EVD', label: 'Evidence' },
  { value: 'RPT', label: 'Report' },
  { value: 'PHT', label: 'Photo' },
  { value: 'VID', label: 'Video' },
  { value: 'AUD', label: 'Audio' },
  { value: 'MAP', label: 'Map' },
  { value: 'LGL', label: 'Legal' },
  { value: 'OTH', label: 'Other' },
];

// Structured grounds for rejecting a case (kept on the case; free-text reason is separate).
export const REJECTION_GROUNDS = ['Out of scope', 'Not a documentable incident', 'Unverifiable / insufficient evidence', 'Inaccurate or fabricated', 'Incomplete data', 'Duplicate', 'Consent or safety concern', 'Other'];

export const STEP_LABELS = [
  'Documenter Details',
  'Data Sharing Agreement',
  'Incident Details',
  'Categories of HR Violations',
  'Perpetrator Details',
  'Victim Details',
  'Impact of HR Violations',
  'Response and Follow-up',
  'Additional Information'
];

export const getViolationTypeLabel = (type: string) => enumLabel(type);

// ---------------------------------------------------------------------------
// Global violation color scheme — single source of truth for violation colors
// across the whole app (form badges, victim pills, case detail). Keys are the
// flat violation keys: top-level types (KILLING/CRIMINALIZATION) and other-types.
// Full static class strings so Tailwind's content scanner keeps them.
// `solid` = filled/selected state, `soft` = light/read-only badge.
// ---------------------------------------------------------------------------
export const VIOLATION_COLORS: Record<string, { solid: string; soft: string }> = {
  KILLING:                   { solid: 'bg-red-600 text-white border-red-600',         soft: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-800' },
  CRIMINALIZATION:           { solid: 'bg-orange-500 text-white border-orange-500',   soft: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-800' },
  ARBITRARY_DETENTION:       { solid: 'bg-amber-500 text-white border-amber-500',     soft: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900 dark:text-amber-200 dark:border-amber-800' },
  PHYSICAL_INJURY:           { solid: 'bg-rose-500 text-white border-rose-500',       soft: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900 dark:text-rose-200 dark:border-rose-800' },
  KIDNAPPING:                { solid: 'bg-purple-600 text-white border-purple-600',   soft: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-800' },
  ENFORCED_DISAPPEARANCE:    { solid: 'bg-violet-600 text-white border-violet-600',   soft: 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900 dark:text-violet-200 dark:border-violet-800' },
  SEXUAL_VIOLENCE:           { solid: 'bg-pink-600 text-white border-pink-600',       soft: 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900 dark:text-pink-200 dark:border-pink-800' },
  ONLINE_ATTACKS:            { solid: 'bg-sky-500 text-white border-sky-500',         soft: 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900 dark:text-sky-200 dark:border-sky-800' },
  FORCED_DISPLACEMENT:       { solid: 'bg-blue-600 text-white border-blue-600',       soft: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800' },
  LAND_GRABBING:             { solid: 'bg-yellow-500 text-white border-yellow-500',   soft: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-800' },
  ENVIRONMENTAL_DESTRUCTION: { solid: 'bg-green-600 text-white border-green-600',     soft: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-800' },
  CULTURAL_DESTRUCTION:      { solid: 'bg-teal-600 text-white border-teal-600',       soft: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900 dark:text-teal-200 dark:border-teal-800' },
  VILIFICATION:              { solid: 'bg-fuchsia-600 text-white border-fuchsia-600', soft: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-900 dark:text-fuchsia-200 dark:border-fuchsia-800' },
  THREATS_AND_INTIMIDATION:  { solid: 'bg-cyan-600 text-white border-cyan-600',       soft: 'bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900 dark:text-cyan-200 dark:border-cyan-800' },
  LOSS_OF_LIVELIHOODS:       { solid: 'bg-lime-600 text-white border-lime-600',       soft: 'bg-lime-100 text-lime-800 border-lime-200 dark:bg-lime-900 dark:text-lime-200 dark:border-lime-800' },
  DISCRIMINATION:            { solid: 'bg-emerald-600 text-white border-emerald-600', soft: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900 dark:text-emerald-200 dark:border-emerald-800' },
  AERIAL_BOMBARDMENT:        { solid: 'bg-slate-600 text-white border-slate-600',     soft: 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-800' },
  FAILURE_TO_FULFILL_ESC_RIGHTS: { solid: 'bg-stone-600 text-white border-stone-600', soft: 'bg-stone-100 text-stone-800 border-stone-200 dark:bg-stone-900 dark:text-stone-200 dark:border-stone-800' },
  FPIC_VIOLATION:            { solid: 'bg-indigo-600 text-white border-indigo-600',   soft: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900 dark:text-indigo-200 dark:border-indigo-800' },
  OTHER:                     { solid: 'bg-gray-500 text-white border-gray-500',       soft: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600' },
};

// Neutral (unselected) pill/badge style — faded, brightens on hover.
export const NEUTRAL_BADGE = 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 opacity-60 hover:opacity-100 hover:bg-gray-50 dark:hover:bg-gray-700';

export const getViolationColor = (key: string) => VIOLATION_COLORS[key] || VIOLATION_COLORS.OTHER;

// Tags IPRI wants applied consistently, offered as one-click suggestions on the case form.
// Add to this list as themes come up; the label is stored verbatim as the tag.
export const SUGGESTED_CASE_TAGS: { label: string; hint: string }[] = [
  { label: 'Just Energy Transition', hint: 'Renewable energy, transition minerals (lithium, nickel, cobalt, copper…), carbon offsets or green infrastructure on Indigenous lands' },
];

